import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  GridRowsProp,
  GridRowModesModel,
  GridEventListener,
  GridRowModel,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';
import { TallyVendor } from '../../../../types/vendor';
import { toast } from 'react-toastify';
import { getVendorColumns } from './vendorDetailsGrid/VendorGridColumns';
import CustomToolbar from './vendorDetailsGrid/CustomToolbar';

interface VendorDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  vendors: TallyVendor[];
  onSaveVendors: (vendors: TallyVendor[]) => void;
}

const VendorDetailsDialog: React.FC<VendorDetailsDialogProps> = ({
  open,
  onClose,
  vendors,
  onSaveVendors,
}) => {
  const initialRows: GridRowsProp = vendors.map((vendor, index) => ({
    id: index,
    ...vendor,
  }));

  const [rows, setRows] = useState(initialRows);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    setRows(vendors.map((vendor, index) => ({ id: index, ...vendor })));
  }, [vendors]);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = useCallback(
    (newRow: GridRowModel) => {
      if (
        !newRow.chartOfAccount ||
        String(newRow.chartOfAccount).trim() === ''
      ) {
        toast.error('Chart of Account cannot be empty.');
        return Promise.reject(new Error('Validation failed'));
      }
      if (!newRow.voucherName || String(newRow.voucherName).trim() === '') {
        toast.error('Voucher Name cannot be empty.');
        return Promise.reject(new Error('Validation failed'));
      }

      const updatedRow = { ...newRow };
      setRows(rows.map(row => (row.id === newRow.id ? updatedRow : row)));
      toast.success('Vendor details updated locally.');
      return updatedRow;
    },
    [rows],
  );

  const handleProcessRowUpdateError = useCallback(() => {
    toast.error('Error updating vendor details.');
  }, []);

  const columns = getVendorColumns(rowModesModel, setRowModesModel);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Vendor Details</DialogTitle>
      <DialogContent>
        {vendors.length === 0 ? (
          <Typography>No vendor details found for this company.</Typography>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={setRowModesModel}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              slots={{
                toolbar: CustomToolbar,
              }}
              slotProps={{
                toolbar: { showQuickFilter: true },
              }}
              sx={{
                boxShadow: 2,
                borderColor: 'blueGrey',
                '& .MuiDataGrid-cell:hover': {
                  color: 'primary.light',
                },
                '& .gridHeader--header': {
                  fontSize: 'large',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: '700',
                  color: '#020617',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
               {isAdding ? (
          <Button disabled>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            Adding Vendors...
          </Button>
        ) : (
          <Button
            onClick={async () => {
              setIsAdding(true);
              try {
                await onSaveVendors(rows as TallyVendor[]);
              } finally {
                setIsAdding(false);
              }
            }}
            disabled={isAdding}
          >
            Add Vendors
          </Button>
        )}
        <Button onClick={onClose} disabled={isAdding}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorDetailsDialog;
